import React, { useEffect } from 'react';
import PrimaryButton from '../../button/PrimaryButton';
import SecondaryButton from '../../button/SecondaryButton';
import './Modal.css';

/**
 * Modal Component
 * Reusable modal dialog
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback when modal should close: () => void
 * @param {string} title - Modal title
 * @param {ReactNode} children - Modal content
 * @param {string} primaryActionLabel - Primary button label
 * @param {function} onPrimaryAction - Primary button click handler
 * @param {string} secondaryActionLabel - Secondary button label
 * @param {function} onSecondaryAction - Secondary button click handler
 * @param {boolean} closeOnBackdropClick - Close on backdrop click (default: true)
 * @param {string} size - Modal size: 'sm'|'md'|'lg'|'xl' (default: 'md')
 * @param {string} className - Additional CSS classes
 */
const Modal = ({
  isOpen = false,
  onClose,
  title,
  children,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  closeOnBackdropClick = true,
  size = 'md',
  className = ''
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div
      className={`modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
    >
      <div className={`modal-content modal-${size} ${className}`.trim()}>
        {title && (
          <div className="modal-header">
            <h3 className="modal-title">{title}</h3>
            <button
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {(primaryActionLabel || secondaryActionLabel) && (
          <div className="modal-footer">
            {secondaryActionLabel && (
              <SecondaryButton onClick={onSecondaryAction || onClose}>
                {secondaryActionLabel}
              </SecondaryButton>
            )}
            {primaryActionLabel && (
              <PrimaryButton onClick={onPrimaryAction}>
                {primaryActionLabel}
              </PrimaryButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;










